/** Recent files: one row per file, newest first, with the stage it has reached - ADR-0212's Upload.
 *
 * Two kinds of row share one table, because a person asks one question - *what happened to what I
 * sent?* - and the answer is in two places. A **document** is what the server holds (read live, so a
 * row cannot disagree with the review screen); a **send** is this visit's attempt that has no
 * document: still sending, interrupted, not sent, or already held. A file the server refused is still
 * a row, with the refusal as its own chip, because a table that dropped the files that failed would
 * read as all-sent.
 *
 * The stage chip is a presentation of the document's state (`stageChip.ts`) and nothing here moves a
 * document. Nothing a bill says is resolved to a site: the Site column is the **uploader's hint**,
 * which the header says is a hint.
 */

import { Link } from "@tanstack/react-router";

import type { DocumentRow } from "../../api/evidence";
import { GridTable, StatusChip } from "../../components/ui";
import type { ChipTone, GridColumn } from "../../components/ui";
import { kindLabel } from "../../lib/documentKinds";
import { fileSize, shortDate } from "../../lib/format";
import { keepSearch } from "../search";
import type { BatchEntry } from "./BatchTable";
import { refusalWord, stageChipOf } from "./stageChip";
import styles from "./RecentFiles.module.css";

export interface FileRow {
  key: string;
  name: string;
  size: number;
  kind: string;
  site: string | null;
  when: string | null;
  /** The chip for the furthest stage, or for how a send stopped. */
  chip: { word: string; tone: ChipTone; stage: string };
  /** The refusal, as its own chip, with the server's code. */
  refusal: { word: string; code: string } | null;
  documentId: string | undefined;
  /** For a duplicate send: the document already held. */
  existingId: string | undefined;
}

const SEND_CHIP: Record<string, { word: string; tone: ChipTone }> = {
  sending: { word: "Sending", tone: "info" },
  failed: { word: "Not sent", tone: "crit" },
  interrupted: { word: "Interrupted", tone: "crit" },
  duplicate: { word: "Already held", tone: "warn" },
};

export function rowOfDocument(document: DocumentRow): FileRow {
  const stage = stageChipOf(document);
  const word = document.state === "quarantined" ? refusalWord(document.refusal_code) : null;
  return {
    key: document.id,
    name: document.original_filename ?? kindLabel(document.kind),
    size: document.byte_size,
    kind: document.kind,
    site: document.facility_hint_name ?? null,
    when: null,
    chip: { word: stage.word, tone: stage.tone, stage: stage.key },
    refusal: word ? { word, code: document.refusal_code ?? "" } : null,
    documentId: document.id,
    existingId: undefined,
  };
}

/** A send of this visit that the list does not show yet. A send whose document the list already
 * holds is that document's row, so it is not listed twice; one that produced a document the list has
 * not caught up with is a row that says *Sent* and can still be opened. */
export function rowOfSend(entry: BatchEntry, known: ReadonlySet<string>): FileRow | null {
  if (entry.documentId && known.has(entry.documentId)) return null;
  const chip = SEND_CHIP[entry.status] ?? { word: "Sent", tone: "neutral" as const };
  return {
    key: entry.key,
    name: entry.name,
    size: entry.size,
    kind: entry.kind,
    site: null,
    when: null,
    chip: { ...chip, stage: entry.status },
    refusal: null,
    documentId: entry.documentId,
    existingId: entry.existingId,
  };
}

export function RecentFiles({
  rows,
  onOpen,
  loading,
  empty,
}: {
  rows: readonly FileRow[];
  onOpen: (row: FileRow) => void;
  loading: boolean;
  empty: React.ReactNode;
}) {
  const columns: GridColumn<FileRow>[] = [
    {
      id: "file",
      header: "File",
      cell: (row) => (
        <span className={styles.file}>
          <span className={styles.name}>{row.name}</span>
          <span className={styles.size}>{fileSize(row.size)}</span>
        </span>
      ),
    },
    { id: "kind", header: "Type", cell: (row) => kindLabel(row.kind) },
    {
      id: "site",
      header: "Site (your hint)",
      cell: (row) => row.site ?? <span className={styles.none}>chosen in review</span>,
    },
    {
      id: "stage",
      header: "Stage",
      cell: (row) => (
        <StatusChip tone={row.chip.tone} size="sm" testId="stage-chip">
          {row.chip.word}
        </StatusChip>
      ),
    },
    {
      id: "result",
      header: "Result",
      cell: (row) => {
        if (row.refusal) {
          return (
            <span data-testid="refusal-chip" data-code={row.refusal.code}>
              <StatusChip tone="crit" size="sm">
                {row.refusal.word}
              </StatusChip>
            </span>
          );
        }
        if (row.existingId) {
          return (
            <Link
              className={styles.link}
              to="/evidence/$documentId"
              params={{ documentId: row.existingId }}
              search={keepSearch}
            >
              Show it
            </Link>
          );
        }
        if (row.documentId && row.chip.stage === "accepted") {
          return <span className={styles.none}>In the ledger</span>;
        }
        return <span className={styles.none}>–</span>;
      },
    },
  ];
  return (
    <GridTable
      caption="Recent files, newest first"
      testId="recent-files"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.key}
      rowLabel={(row) => row.name}
      onRowOpen={(row) => (row.documentId ? onOpen(row) : undefined)}
      loading={loading}
      empty={empty}
    />
  );
}

// Kept for the file's own readers: a date for a row that has one.
export { shortDate };
